import { useCallback } from "react"

import ArticleClassInfo, { ArticleClass, ArticleClassField } from "models/ArticleClassInfo"
import ArticleService from "services/ArticleService"
import toastMessages from "stores/toastMessages"
import { createEffect, createStore } from "effector"
import CustomError from "models/CustomError"
import { useUnit } from "effector-react"
import { without } from "lodash"

const $articleClassesInfo = createStore<ArticleClassInfo[] | null>(null)

const fetchArticleClasses = createEffect<void, ArticleClassInfo[], CustomError>(async () => {
    return ArticleService.getArticleClasses()
})

$articleClassesInfo.on(fetchArticleClasses.doneData, (_, articleClassesInfo) => articleClassesInfo)

export default function useArticleClasses() {
    const articleClassesInfo = useUnit($articleClassesInfo)

    const fetchArtcleClasses = useCallback(async () => {
        if (articleClassesInfo) return
        try {
            await fetchArticleClasses()
        } catch (e) {
            console.error(e)
            toastMessages.showSystemError()
        }
    }, [articleClassesInfo])

    const getArticleClassVisibleFields = useCallback((articleClass: ArticleClass) => {
        if (!articleClassesInfo) return
        return articleClassesInfo.find(ac => ac.class === articleClass)?.visibleFields
    }, [articleClassesInfo])

    const articleClassHasField = useCallback((articleClass: ArticleClass | null | undefined, articleClassField: ArticleClassField) => {
        if (!articleClass || !articleClassesInfo) return false
        const visibleFields = getArticleClassVisibleFields(articleClass)
        return !!visibleFields?.includes(articleClassField)
    }, [articleClassesInfo, getArticleClassVisibleFields])

    // dimensions, voulme and weight are available for all classes,
    // so we search for the other fields
    const getArticleClassSpecificFields = useCallback((articleClass: ArticleClass | null | undefined) => {
        if (!articleClass) return []
        return without(getArticleClassVisibleFields(articleClass), ArticleClassField.dimensions, ArticleClassField.volume, ArticleClassField.weight)
    }, [getArticleClassVisibleFields])

    return {
        fetchArtcleClasses,
        articleClassHasField,
        getArticleClassSpecificFields,
    }

}
